import { Button } from "@ai-teach/ui-web";
import type { ReactNode } from "react";
import { useState } from "react";
import { HiMoon, HiSun } from "react-icons/hi";
import { LuSettings, LuX } from "react-icons/lu";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * Navigation item configuration
 */
export interface NavItem {
  label: string;
  icon: React.ReactNode;
  to: string;
  /** Extra prefixes this item owns — classroom pages sit outside the school list they open from. */
  matchPaths?: string[];
}

/**
 * Base sidebar navigation component
 * Provides the core layout with settings item and accepts custom nav items as children
 */
export interface SidebarProps {
  /** Custom navigation items to render above the settings item */
  children?: ReactNode;
  /** Active item ID for highlighting the settings item */
  activeItem?: string;
  /** Secondary nav items shown in the settings panel */
  settingsItems?: NavItem[];
  /** Navigates to a settings item's route */
  onNavigate?: (to: string) => void;
}

export function Sidebar({ children, activeItem, settingsItems = [], onNavigate }: SidebarProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    setIsPanelOpen(false);
    await signOut();
  };

  const isSettingsActive =
    activeItem === "settings" || settingsItems.some((item) => item.label === activeItem);

  return (
    <>
      <nav className="w-20 h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col items-center py-6 gap-2 overflow-y-auto shadow-sm">
        {children}

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="nav-glow-effect nav-item-inactive mt-auto w-16 h-16 flex-col text-gray-700 dark:text-gray-200"
        >
          {theme === "dark" ? <HiSun fontSize={24} /> : <HiMoon fontSize={24} />}
          <span className="caption">{theme === "dark" ? "Light" : "Dark"}</span>
        </button>

        <button
          type="button"
          onClick={() => setIsPanelOpen((open) => !open)}
          aria-label="Settings"
          aria-expanded={isPanelOpen}
          aria-current={isSettingsActive ? "page" : undefined}
          className={`nav-glow-effect ${isSettingsActive || isPanelOpen ? "nav-item-active" : "nav-item-inactive"} w-16 h-16 flex-col`}
        >
          <LuSettings fontSize={24} />
          <span className="caption">Settings</span>
        </button>
      </nav>

      {/* Settings slide-out panel */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-200 ${isPanelOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setIsPanelOpen(false)}
        onKeyDown={(e) => e.key === "Escape" && setIsPanelOpen(false)}
      />

      <aside
        className={`fixed left-20 top-0 z-50 h-full w-64 flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 shadow-xl transition-all duration-200 ${isPanelOpen ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0 pointer-events-none"}`}
      >
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="h6">Settings</h2>
          <button
            type="button"
            onClick={() => setIsPanelOpen(false)}
            aria-label="Close settings"
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-secondary-200"
          >
            <LuX fontSize={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-1">
          {settingsItems.map((item) => {
            const isActive = activeItem === item.label;

            return (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  onNavigate?.(item.to);
                  setIsPanelOpen(false);
                }}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
                className={`nav-glow-effect ${isActive ? "nav-item-active" : "nav-item-inactive"} w-full justify-start gap-3 px-3 py-2.5`}
              >
                {item.icon}
                <span className="body-sm">{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
          <Button variant="danger" onClick={handleLogout} className="w-full">
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}
